import React, { useEffect } from 'react';
import {
  withStyles,
} from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  setFillinValues,
  deleteFillinItem,
  searchFillinList,
  restoreOriginalFillin,
  createFillinItem,
  updateFillinList,
} from '~/store/dailyparts/actions';
import { getLoads, getEquipmentsGroups } from '~/store/manager/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate,
  formatTime,
  newMomentDate,
  parseDate,
  parseDateTimeToTimestamp,
  subtractDaysFromNow,
} from '~/utils/moment';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import PF2MMaskTextField from '~/components/PF2MMaskTextField';
import { usePagination } from '../hooks';

const FILLIN_EQUIPMENTS = [29];
const TRUCK_ID = 2;

const FillinList = () => {
  const { t: translate } = useTranslation();

  const filters = useSelector(state => state.dailyParts.filters);

  const fillinList = useSelector(state => state.dailyParts.fillinList);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const equipments = useSelector(state => state.manager.equipments);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators
    .filter(o => o.name), shallowEqual);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const originalFillinList = useSelector(state => state.dailyParts.originalFillinList);
  const fillinPagination = useSelector(
    state => state.dailyParts.pagination.fillinPagination,
  );

  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(
    userAccessLevel,
  );
  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosureConfig } = usePlatformConfiguration('enable_closure');

  const dispatch = useDispatch();

  useEffect(() => {
    async function loadData() {
      await Promise.all([
        dispatch(getLoads(filters.equipmentType)),
        dispatch(getEquipmentsGroups(true)),
      ]);
    }

    if (filters.equipmentType) {
      loadData();
    }
  }, [filters.equipmentType, dispatch]);

  const formatTimestampToDate = (timestamp) => {
    if (!timestamp) return '';
    return formatDate(timestamp, true);
  };

  const renderDatePicker = (rowData, key, t, userLimit, enableClosureFlag, closureList) => {
    let userLimitEdit = subtractDaysFromNow(userLimit);

    if (enableClosureFlag && closureList?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closureList[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimit);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimit
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: t('validation:InvalidDate') };
    }

    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const renderTime = (rowData, attr) => (
    <PF2MMaskTextField
      mask="99:99:99"
      value={rowData[attr]}
      onChange={(e) => {
        const { value } = e.target;
        const newValue = value.replace(/_/g, '0');
        dispatch(setFillinValues(rowData, attr, newValue));
      }}
    />
  );

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      render: rowData => formatTimestampToDate(rowData.start_date),
      validate: rowData => renderDatePicker(
        rowData,
        'start_date',
        translate,
        userLimitEditConfig,
        enableClosureConfig,
        closures,
      ),
    },
    {
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      render: rowData => formatTimestampToDate(rowData.end_date),
      validate: rowData => renderDatePicker(
        rowData,
        'end_date',
        translate,
        userLimitEditConfig,
        enableClosureConfig,
        closures,
      ),
    },
    {
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      render: rowData => renderTime(rowData, 'start_time'),
      validate: (rowData) => {
        const isValid = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(rowData.start_time);
        return isValid
          ? { isValid: true }
          : { isValid: false, helperText: translate('validation:InvalidTime') };
      },
    },
    {
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      render: rowData => renderTime(rowData, 'end_time'),
      validate: (rowData) => {
        if (!rowData.end_time) return { isValid: true };
        const isValid = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(rowData.end_time);
        return isValid
          ? { isValid: true }
          : { isValid: false, helperText: translate('validation:InvalidTime') };
      },
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      customType: 'number',
    },
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
    },
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
      customType: 'filterLookup',
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: equipmentsGroups.filter(e => FILLIN_EQUIPMENTS.includes(e.id_equipament)),
      customType: 'filterLookup',
      customOnChange: () => ({
        group_equipament_links_id: '',
      }),
    },
    {
      title: translate('common:Equipment'),
      field: 'group_equipament_links_id',
      lookup: equipments,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group) === String(rowData.equip_group_id),
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        operator_id: '',
      }),
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.operator_group_id),
    },
    {
      title: translate('common:TruckEquipment'),
      field: 'truck_id',
      lookup: equipments.filter(e => e.id_equip === TRUCK_ID),
      customType: 'filterLookup',
    },
    {
      title: translate('common:Batch'),
      field: 'batch_number',
      customType: 'number',
    },
    {
      title: `${translate('common:LoadDefault')} (t)`,
      field: 'load_manager',
      customType: 'number',
    },
    {
      title: translate('common:StopTime'),
      field: 'stop_time',
      customType: 'number',
    },
    {
      title: translate('common:LoadTime'),
      field: 'load_time',
      customType: 'number',
    },
    {
      title: translate('common:CodeTime'),
      field: 'code_time',
      customType: 'number',
    },
    {
      editable: 'never',
      title: translate('common:CycleType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
    },
  ];

  const filterToFormFieldMap = {
    turn: 'turn_id',
    equipmentGroup: 'equip_group_id',
    equipment: 'group_equipament_links_id',
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
  };

  const convertIfNeeded = (value, key) => {
    if (key === 'codeType' || key === 'codeGroup') {
      return Number(value);
    }
    return value;
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];

      if (filterValue !== undefined && filterValue !== 0) {
        if (Array.isArray(filterValue) && filterValue.length === 1) {
          const [firstValue] = filterValue;
          if (firstValue !== undefined) {
            acc[formField] = convertIfNeeded(firstValue, filterKey);
          }
        } else {
          acc[formField] = convertIfNeeded(filterValue, filterKey);
        }
      }

      return acc;
    },
    {},
  );


  const disableSaveAllChangesButton = fillinList?.length ? fillinList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => columns.find(col => col.field === key)?.required && value === '')
    ))
    .includes(true) : false;

  const initialFormData = {
    ...filters,
    ...mappedFilterData,
    duration: 0,
    turn_id: '',
    team_id: '',
    equip_group_id: '',
    equip_id: 29,
    group_equipament_links_id: '',
    truck_id: '',
    batch_number: '',
    load_manager: '',
    stop_time: '',
    load_time: '',
    code_time: '',
    operator_id: '',
    timestamp_usn: '',
    uid: '',
    tablet_id: '',
    start_date: formatDate(newMomentDate(), true),
    end_date: formatDate(newMomentDate(), true),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'turn',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'material',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
      'equip_id',
      'operator_group_id',
      'handlingType',
    ];

    fieldsToRemove.forEach((fieldName) => {
      delete result[fieldName];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'fillin',
    pagination: fillinPagination,
    searchAction: searchFillinList,
  });

  const { page = 0, total = 0, loadingMore = false } = fillinPagination || { };

  return (
    <PF2MDataTable
      title={translate('common:Cycles')}
      columns={applyDefaultSort(columns)}
      data={fillinList}
      initialFormData={initialFormData}
      page={page}
      totalCount={total}
      isLoading={loadingMore}
      options={{
        pageSize: 10,
        pageSizeOptions: [10, 20, 50, 100],
        showTitle: true,
        selection: true,
        search: false,
        cacheKey: 'fillin_list',
        maxBodyHeight: 'calc(100vh - 290px)',
        minBodyHeight: 'calc(100vh - 290px)',
        disableSaveButton: disableSaveAllChangesButton,
        showFirstLastPageButtons: false,
        ...paginationOptions,
      }}
      onAdd={(newItem) => {
        const createParams = {
          ...newItem,
          ...mappedFilterData,
        };
        dispatch(createFillinItem(createParams));
      }}
      onDiscard={() => dispatch(restoreOriginalFillin())}
      onApply={() => {
        const parsedData = fillinList
          .filter(item => item.update_status)
          .map(removeUnnecessaryFields);

        const actionData = {
          data: parsedData,
          parameters: filters,
          saveLog: true,
          ogData: originalFillinList,
        };
        dispatch(updateFillinList(actionData));
      }}
      onChange={(newData) => {
        newData.forEach(({ ...row }) => {
          dispatch(setFillinValues(row, row));
        });
      }}
      onDelete={(newData) => {
        newData.forEach((e) => {
          dispatch(deleteFillinItem(e));
        });
      }}
    />
  );
};

FillinList.defaultProps = {};

export default withStyles(styles)(FillinList);
